import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { memo, type ReactNode } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Expand01Icon, MoveIcon, Trash01Icon } from '@/components/ui/icons'
import {
	OpsVettingDisabledContainer,
	OpsVettingDragButton,
	OpsVettingGalleryItemsContainer,
	OpsVettingImageTitle
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n'

interface ProPhotosGalleryItemProps {
	imageUrl: string
	imageName: string
	onClickExpand: () => void
	bottomLeftSlot: ReactNode
	isPendingImage: boolean
	isHostApprovalNeeded: boolean
	isProPhotography: boolean
	imageId: number
	onDeleteModalOpen: (e: React.MouseEvent<HTMLButtonElement>) => void
}

export const ProPhotosGalleryItem = memo(
	({
		imageUrl,
		bottomLeftSlot,
		isPendingImage,
		isHostApprovalNeeded,
		isProPhotography,
		imageId,
		imageName,
		onClickExpand,
		onDeleteModalOpen
	}: ProPhotosGalleryItemProps) => {
		const { t } = useTranslation()
		const {
			attributes,
			setNodeRef,
			transform,
			transition,
			isDragging,
			listeners
		} = useSortable({ id: imageId })

		const style = {
			transform: CSS.Translate.toString(transform),
			transition
		}

		return (
			<OpsVettingDisabledContainer
				$isDisabled={isDragging}
				ref={setNodeRef}
				style={style}
				{...attributes}
			>
				<OpsVettingGalleryItemsContainer
					direction={'column'}
					fullWidth
					gap={4}
				>
					<GalleryImageItem
						bottomLeftSlot={bottomLeftSlot}
						bottomRightSlot={
							<ButtonLegacy
								className='show-on-hover'
								onClick={onClickExpand}
								shape='square'
								size='sm'
								type='button'
								variant='secondary'
							>
								<Expand01Icon />
							</ButtonLegacy>
						}
						src={imageUrl}
						topLeftSlot={
							<OpsVettingDragButton
								{...listeners}
								$isDragging={isDragging}
								className='show-on-hover'
								shape='square'
								size='sm'
								type='button'
								variant='secondary'
							>
								<MoveIcon size={16} />
							</OpsVettingDragButton>
						}
						topRightSlot={
							<ButtonLegacy
								className='show-on-hover'
								onClick={onDeleteModalOpen}
								shape='square'
								type='button'
								variant={'secondary'}
							>
								<Trash01Icon />
							</ButtonLegacy>
						}
					/>

					<div className='flex w-full flex-col gap-1'>
						<OpsVettingImageTitle>{imageName}</OpsVettingImageTitle>
						<div className='flex w-full items-center justify-between gap-1.5'>
							{isProPhotography ? (
								<span className='text-neutral-600 text-sm'>
									{t('vetting:reordering.pro_photo')}
								</span>
							) : (
								isHostApprovalNeeded === false && (
									<span className='text-neutral-600 text-sm'>
										{t('vetting:reordering.host_provided')}
									</span>
								)
							)}

							{isPendingImage && (
								<Chip
									isActive
									label={t('vetting:reordering.pending')}
									shape='roundedRectangle'
									startSlot={
										<span className='ml-1 h-2 w-2 rounded-full bg-warning-500' />
									}
									variant={'warning'}
								/>
							)}
						</div>
					</div>
				</OpsVettingGalleryItemsContainer>
			</OpsVettingDisabledContainer>
		)
	}
)
